// The Conveyor behaviour: a condition the Jetson runs on its own. It is not run in the cloud; the sequence
// leaves as a file (Export for Jetson on the sequence row), compiled once by the VLM box. Design:
// agentic-workflow/docs/jetson-edge/2026-10-13-edge-sequence-export-design.md.
export const CONVEYOR_MODE = 'conveyor';

export const CONVEYOR_PLACEHOLDER =
  'Find all paper cups with defects inside A1.\nReferences 1-3 are defective, 4-6 are normal.';

// Short on purpose (Yeskendir, 2026-10-15): the placeholder already teaches the words.
export const CONVEYOR_HINT = 'Preparing a condition for conveyor mode.';

// The Jetson checks only the centre 416x416 of its camera's 1280x720 left eye, uncut and
// unscaled (edge runtime sources.py crop_box). The backend carries marks drawn on a full
// frame into that square the same way (3d-backend jetsonView.js).
const EYE = { width: 1280, height: 720 };
const SIDE = 416;
const ASPECT_TOLERANCE = 0.01;

// The square the Jetson sees on a width x height photo, in its pixels: a square photo is
// the device's view already; a 16:9 frame (scaled or not) gives the centre 416 of 720;
// a two-eye frame the same on its left eye; any other photo its centre square.
export function jetsonCrop(width, height) {
  if (width === height) return { x: 0, y: 0, side: width, jetson: true };
  const aspect = width / height;
  const near = target => Math.abs(aspect - target) <= ASPECT_TOLERANCE * target;
  const side = (SIDE * height) / EYE.height;
  if (near(EYE.width / EYE.height)) {
    return {
      x: (width - side) / 2,
      y: (height - side) / 2,
      side,
      jetson: true,
    };
  }
  if (near((2 * EYE.width) / EYE.height)) {
    return {
      x: (width / 2 - side) / 2,
      y: (height - side) / 2,
      side,
      jetson: true,
    };
  }
  const square = Math.min(width, height);
  return {
    x: (width - square) / 2,
    y: (height - square) / 2,
    side: square,
    jetson: false,
  };
}

// A photo attached to a Conveyor sequence becomes the square the Jetson checks, so the
// areas are drawn on what the device sees, as large as the canvas allows. A frame
// downloaded from the device in crop mode is that square and comes back as it is.
// Resolves {file, crop} (crop null when nothing was cut).
export async function toJetsonView(file) {
  if (!String(file?.type || '').startsWith('image/'))
    return { file, crop: null };
  const bitmap = await createImageBitmap(file);
  const { width, height } = bitmap;
  const crop = jetsonCrop(width, height);
  if (crop.side === width && crop.side === height) {
    bitmap.close();
    return { file, crop: null };
  }
  const side = Math.round(crop.side);
  const canvas = document.createElement('canvas');
  canvas.width = side;
  canvas.height = side;
  canvas
    .getContext('2d')
    .drawImage(
      bitmap,
      Math.round(crop.x),
      Math.round(crop.y),
      side,
      side,
      0,
      0,
      side,
      side,
    );
  bitmap.close();
  // PNG: the frame is cut, not re-compressed, so what is drawn on is the device's pixels
  const blob = await new Promise(resolve =>
    canvas.toBlob(resolve, 'image/png'),
  );
  const name = String(file.name || 'frame').replace(/\.[^.]+$/, '');
  return {
    file: new File([blob], `${name}-jetson.png`, { type: 'image/png' }),
    crop: { ...crop, width, height },
  };
}

export const isConveyorMode = mode => mode === CONVEYOR_MODE;

// Only a sequence with a Conveyor condition can be exported for the Jetson. The list carries the backend's flag;
// the steps, loaded for the selected sequence, show a condition saved since the list was read.
export function hasConveyorCondition(sequence) {
  return Boolean(
    sequence?.conveyor ||
    (sequence?.steps || []).some(step =>
      (step.conditions || []).some(condition => isConveyorMode(condition.mode)),
    ),
  );
}

export function conveyorFileName(sequenceName) {
  return `${String(sequenceName || 'sequence').replace(/[\\/:*?"<>|]+/g, '-')}.ohive-edge.json`;
}

export function saveConveyorFile(file, sequenceName) {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(
    new Blob([JSON.stringify(file)], { type: 'application/json' }),
  );
  link.download = conveyorFileName(sequenceName);
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}
